import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useHistoryContext } from "~/routes/context";
import {
  getDisplayModeProjectTasksByProjectId,
  getPersonalizedConfigOption,
  getRawPersonalizedConfigOption,
  getUpdateHideNotifications,
} from "~/store/selectors/selector.configOptions";
import { makeTaskNameByIdSelector } from "~/store/selectors/selector.tasks";
import { makeGetTeamUsers } from "~/store/selectors/selector.teams";
import * as components from "./task-navigation";
import { usePageTitle, useUnreadCount } from "../Hooks";

const types = {
  listColumnNavigation: "ListColumnNavigation",
  timeLineNavigation: "TimeLineNavigation",
  myWorkByDateNavigation: "MyWorkByDateNavigation",
  myWorkByProjectNavigation: "MyWorkByProjectNavigation",
  teamsByAssigneeNavigation: "TeamsByAssigneeNavigation",
  "no-navigation": "TaskSheet",
};

export const TaskSheetWrapper = React.forwardRef(({ open, id, projectId, onClose }, ref) => {
  const { pathname } = useLocation();

  const isProjectIdEqualTaskProjectId = Number(pathname.split("/")[2]) === Number(projectId);

  const myWorkGroupBy = useSelector((state) => getPersonalizedConfigOption(state, "my_work_tasks_grouped_by"));

  const userProfileGroupBy = useSelector((state) => getPersonalizedConfigOption(state, "user_profile_tasks_group_by"));

  const teamTasksGroupedBy = useSelector((state) => getPersonalizedConfigOption(state, "team_tasks_group_by"));

  const displayMode = useSelector((state) => getDisplayModeProjectTasksByProjectId(state, projectId));
  const myWorkSection = useSelector((state) => getRawPersonalizedConfigOption(state, "my_work_selected_tab"));

  const getTaskNameById = useMemo(makeTaskNameByIdSelector, []);
  const { state } = useHistoryContext();
  const prevPageTitle = state[state.length - 1]?.title;

  const updatesHideNotifications = useSelector(getUpdateHideNotifications);
  const totalUnreadUpdates = useUnreadCount();
  const taskName = useSelector((state) => getTaskNameById(state, id));

  usePageTitle(
    open ? taskName : prevPageTitle ? prevPageTitle : "ActiveCollab",
    !updatesHideNotifications ? totalUnreadUpdates : 0
  );

  const regexProjectTasks = /^\/projects\/\d+$/;
  const regexMyWork = "my-work";
  const regexTeams = useMemo(() => /^\/teams\/\d+/, []);
  const regexUserProfile = useMemo(() => /^\/users\/\d+\/tasks$/, []);

  const showMyWorkByDateNavigation =
    open &&
    pathname.match(regexMyWork) &&
    myWorkSection === "tasks" &&
    (myWorkGroupBy === "start_date" || myWorkGroupBy === "date");

  const showMyWorkByProjectNavigation =
    open && pathname.match(regexMyWork) && myWorkSection === "tasks" && myWorkGroupBy === "project";

  const showUserProfileByProjectNavigation =
    open && !!pathname.match(regexUserProfile) && userProfileGroupBy === "project";

  const showUserProfileByDateNavigation =
    open &&
    !!pathname.match(regexUserProfile) &&
    (userProfileGroupBy === "start_date" || userProfileGroupBy === "date");

  const showTeamTasksByProjectNavigation = open && !!pathname.match(regexTeams) && teamTasksGroupedBy === "project";

  const showTeamTasksByDateNavigation =
    open && !!pathname.match(regexTeams) && (teamTasksGroupedBy === "start_date" || teamTasksGroupedBy === "date");

  const showTeamTasksByAssigneeNavigation = open && !!pathname.match(regexTeams) && teamTasksGroupedBy === "assignee";

  const teamId = useMemo(() => {
    if (pathname.match(regexTeams)) {
      return +pathname.match(/\d+/)[0];
    }

    return undefined;
  }, [pathname, regexTeams]);

  const getTeamMembers = useMemo(() => makeGetTeamUsers(teamId), [teamId]);

  const teamMembers = useSelector(getTeamMembers);

  const userIdsArray = useMemo(() => teamMembers.map((member) => member.id), [teamMembers]);

  const userId = useMemo(() => {
    if (pathname.match(regexUserProfile)) {
      return +pathname.match(/\d+/)[0];
    }

    if (pathname.match(regexTeams)) {
      return userIdsArray;
    }

    return undefined;
  }, [pathname, regexTeams, regexUserProfile, userIdsArray]);

  const showListColumnNavigation =
    open &&
    isProjectIdEqualTaskProjectId &&
    pathname.match(regexProjectTasks) &&
    (displayMode === "list" || displayMode === "column");

  const showTimelineNavigation =
    open && isProjectIdEqualTaskProjectId && pathname.match(regexProjectTasks) && displayMode === "timeline";

  const renderInner = useMemo(() => {
    let type = "no-navigation";

    if (showMyWorkByDateNavigation || showUserProfileByDateNavigation || showTeamTasksByDateNavigation) {
      type = "myWorkByDateNavigation";
    } else if (showListColumnNavigation) {
      type = "listColumnNavigation";
    } else if (showTimelineNavigation) {
      type = "timeLineNavigation";
    } else if (
      showMyWorkByProjectNavigation ||
      showUserProfileByProjectNavigation ||
      showTeamTasksByProjectNavigation
    ) {
      type = "myWorkByProjectNavigation";
    } else if (showTeamTasksByAssigneeNavigation) {
      type = "teamsByAssigneeNavigation";
    }
    return type;
  }, [
    showMyWorkByDateNavigation,
    showUserProfileByDateNavigation,
    showTeamTasksByDateNavigation,
    showListColumnNavigation,
    showTimelineNavigation,
    showMyWorkByProjectNavigation,
    showUserProfileByProjectNavigation,
    showTeamTasksByProjectNavigation,
    showTeamTasksByAssigneeNavigation,
  ]);

  const type = types[renderInner] ?? "no-navigation";
  const Component = components[type];

  return (
    <Component
      ref={ref}
      taskId={id}
      projectId={projectId}
      onClose={onClose}
      open={open}
      showControls={renderInner !== "no-navigation"}
      userId={userId}
    />
  );
});

TaskSheetWrapper.propTypes = {
  onClose: PropTypes.func,
  id: PropTypes.number,
  projectId: PropTypes.number,
  open: PropTypes.bool,
};

TaskSheetWrapper.displayName = "TaskSheetWrapper";
